import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { DataView as PrimeDataList } from 'primeng/dataview';
import { MomentService, UtilService } from 'src/app/shared';

import { EventRead } from '../shared/event-read.model';
import { EventSearchParams } from '../shared/event-search-params.model';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { AsyncState } from 'src/app/shared/model';
import { PagedResult } from 'src/app/shared/model/generic-paged-result.model';
import { EventsV4Service } from '../shared/events-v4.service';

/**
 * modal for selecting an event
 */
@Component({
  selector: 'wl-event-v4-select-event',
  templateUrl: 'event-v4-select-event.component.html',
  styleUrls: ['event-v4-select-event.component.css'],
  providers: [EventsV4Service],
    standalone: false
})
export class EventV4SelectEventComponent {
  /**
   * starting search params or default if not given
   */
  @Input() public searchParams: EventSearchParams = new EventSearchParams();

  /**
   * select button text. Default is 'select'
   */
  @Input() public selectButtonText: string = '';

  /**
   * select button glyphicon. example: 'plus'. none used if blank
   */
  @Input() public selectButtonGlyphIcon: string = '';

  /**
   * selected event when selected
   */
  @Output() public onEventSelect: EventEmitter<EventRead> = new EventEmitter<EventRead>();

  private _momentService: MomentService;
  public _modelChangeDebounceTimeoutId: number = 0;
  public _searching: AsyncState = new AsyncState();
  public _results: PagedResult<Array<EventRead>> = new PagedResult<Array<EventRead>>([]);
  @ViewChild('eventsResultList', { static: false }) public _eventsResultList: PrimeDataList;

  /**
   * injection
   * @param _eventsService
   * @param _userService
   * @param _navigationService
   */
  public constructor(
    public _eventsService: EventsV4Service,
    public _navigationService: NavigationService,
    momentService: MomentService,
    public _utilService: UtilService) {
    this._momentService = momentService;

    this.resetForm();
    this.searchParams.OrderBy = 'EventDate:asc';
    this.searchParams.PageSize = 10;
  }

  /**
   * reset to start of results and load first page of events
   */
  public search() {
    // triggers lazy load which triggers loading of events.
    (<any>this._eventsResultList).first = 0;
    //this._eventsResultList.updatePaginator();
    this.searchParams.PageNumber = 1;
    this.getPageOfEvents();
  }

  /**
   * rest form to default
   */
  public resetForm(): void {
    this.searchParams.Keyword = '';
    this.searchParams.EventType = [];
    this.searchParams.OrganizationEvent = this.searchParams.InternalEvent = this.searchParams.MembersOnlyEvent = true;
    this.searchParams.CommunityEvent = false;
    this.searchParams.SearchDateBegin = this._momentService.getMoment().startOf('day').toDate();
    this.searchParams.SearchDateEnd = this._momentService.getMoment().add(1, 'years').startOf('day').toDate();
  }

  /**
   * load next page of events
   */
  public getPageOfEvents(): void {
    // no quick search on event page
    if (this._searching.busy) return;

    this._searching.busy = true;
    this._searching.success = false;
    this._searching.message = '';

    this._eventsService.searchEvents(this.searchParams).then(
      (result: any) => {
        this._searching.busy = false;
        this._searching.success = true;
        this._searching.message = '';

        this._results = result;
      },
      (error: any) => {
        this._searching.busy = false;
        this._searching.success = false;
        this._searching.message = this._utilService.extractErrorMessage(error);
      }
    );
  }

  /**
   * triggerd on model change
   */
  public handleModelChange(): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    // no quick search on event page
    if (this._searching.busy) return;

    if (this.searchParams.Keyword && this.searchParams.Keyword.trim().length > 4)
      this._modelChangeDebounceTimeoutId = window.setTimeout(() => this.search(), 400);
  }

  /**
   * triggerd when reset button clicked
   */
  public lazyLoad($event: any): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    // prime's lazyLoad event fires during angular init, so _make_ sure you don't handle it after the detect changes cycle
    setTimeout(() => {
      this.searchParams.PageNumber = $event.first / Math.max(1, $event.rows) + 1;
      this.searchParams.PageSize = $event.rows;
      this.getPageOfEvents();
    });
  }

  /**
   * triggerd when reset button clicked
   */
  public handleResetClick(): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    this.resetForm();
  }

  /**
   * triggerd clicking on the select button for an event
   * @param selectedEvent
   */
  public handleSelectEventClick(selectedEvent: EventRead): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);
    this.onEventSelect.emit(selectedEvent);
  }

  /**
   * triggerd when submitting the form
   */
  public handleSearchFormSubmit(): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    this.search();
  }
}
